import {
  Client,
  Conversation,
  Participant,
  Paginator,
  Message,
} from "@twilio/conversations";
import {
  AgentEvent,
  Callback,
  chatEndedByValue,
  ConversationEventType,
  InitializeConversationClientArgs,
  SendMessageData,
  ConversationMetadata,
  ConversationEvent,
  ConversationServiceReadyEvent,
  ConversationUpdateEvent,
} from "./definitions/types";
import { AgentActions, ConversationEvents } from "./definitions/constants";
import { copy } from "./definitions/copy";
import { HelpCenterApi } from "@twiliointernal/e2-commons-shared-api";
import { HelpCenterApiArgs } from "@twiliointernal/e2-commons-shared-definitions";

class ConversationService {
  private client: Client | undefined;
  private conversation: Conversation | undefined;
  private chatIdentity: string | undefined;
  private helpCenterApi: HelpCenterApi | undefined;

  private listeners: {
    [K in ConversationEventType]?: Callback<ConversationEvent>[];
  } = {};

  isServiceReady = false;

  initTypingEvents = (conversation: Conversation) => {
    const handleTypingEvent =
      (isTyping: boolean) => (participant: Participant) => {
        if (participant.identity !== this.chatIdentity) {
          this.listeners[ConversationEvents.Typing]?.forEach((listener) =>
            listener(isTyping)
          );
        }
      };

    conversation.on("typingStarted", handleTypingEvent(true));
    conversation.on("typingEnded", handleTypingEvent(false));
  };

  initMessageEvents = (conversation: Conversation) => {
    const updateMessages = async () => {
      const messages = await this.fetchAllMessages(conversation);
      this.listeners[ConversationEvents.Messages]?.forEach((listener) =>
        listener(messages)
      );
    };

    conversation.on("messageAdded", updateMessages);
    conversation.on("messageUpdated", updateMessages);
    conversation.on("messageRemoved", updateMessages);
    updateMessages();
  };

  initParticipantEvents = async (conversation: Conversation) => {
    const handleParticipantEvent =
      (action: AgentEvent["action"]) => async (participant: Participant) => {
        const agent = this.getAgentDetails(participant);

        const event = { action, agent };

        this.listeners[ConversationEvents.Agent]?.forEach((listener) =>
          listener(event)
        );
      };

    conversation.on(
      "participantLeft",
      handleParticipantEvent(AgentActions.Leave)
    );
    conversation.on(
      "participantJoined",
      handleParticipantEvent(AgentActions.Join)
    );
  };

  initUpdatedEvents = (conversation: Conversation) => {
    conversation.on("updated", (event: ConversationUpdateEvent) => {
      this.listeners[ConversationEvents.Updated]?.forEach((listener) =>
        listener(event)
      );
    });
  };

  // Init all event listeners from SDK
  initConversation = async (conversation: Conversation) => {
    this.initTypingEvents(conversation);
    this.initMessageEvents(conversation);
    this.initParticipantEvents(conversation);
    this.initUpdatedEvents(conversation);
  };

  initHelpCenterApi = (props: HelpCenterApiArgs) => {
    this.helpCenterApi = new HelpCenterApi(props);
  };

  getAgentDetails = ({ sid, identity }: Participant) => {
    return {
      sid,
      identity: identity || "",
    };
  };

  handleTokenError = (message: string) => {
    console.error("Token Error:", message);
    this.setError(copy.errors.authToken.refreshFailed);
  };

  refreshAuthToken = async () => {
    if (!this.chatIdentity) {
      this.handleTokenError(copy.errors.authToken.identityNotFound);
      return;
    }

    if (!this.helpCenterApi) {
      this.handleTokenError("HelpCenterApi not initialized");
      return;
    }

    const response = await this.helpCenterApi.refreshToken({
      identity: this.chatIdentity,
    });

    if (!response.data.token) {
      this.handleTokenError(copy.errors.authToken.refreshFailed);
      return;
    }

    await this.client?.updateToken(response.data.token);
  };

  composeConversationMetadata = async (
    conversation: Conversation,
    identity: string
  ): Promise<ConversationMetadata> => {
    try {
      const participants = await conversation.getParticipants();
      const customer = participants.find(
        (participant) => participant.identity === identity
      );

      const conversationMetadata = {
        agent: null,
        attributes: conversation.attributes,
        customer: { sid: customer?.sid || "", identity },
        dateCreated: conversation.dateCreated,
      } as ConversationMetadata;

      const agent = participants.find(
        (participant) => participant.identity !== identity
      );

      if (agent) {
        conversationMetadata.agent = this.getAgentDetails(agent);
      }

      return conversationMetadata;
    } catch (err) {
      console.error("composeConversationMetadata", err);
      throw err;
    }
  };

  fetchAllMessages = async (conversation: Conversation) => {
    let allMessages: Message[] = [];
    let paginator: Paginator<Message> | null = await conversation.getMessages();

    while (paginator) {
      allMessages = [...paginator.items, ...allMessages];
      if (paginator.hasPrevPage) {
        paginator = await paginator.prevPage();
      } else {
        paginator = null;
      }
    }

    return allMessages;
  };

  setError = (errorMessage: string) => {
    if (errorMessage) {
      console.error(errorMessage); // TODO: Used here only for development purposes
      this.listeners[ConversationEvents.Error]?.forEach((listener) =>
        listener(errorMessage)
      );
    }
  };

  initService = ({
    token,
    liveChatId,
    identity,
  }: InitializeConversationClientArgs) => {
    if (!token || token.length < 32) {
      this.setError(copy.errors.authToken.invalidToken);
      return false;
    }

    if (!liveChatId || liveChatId.length < 32) {
      // throw new Error("Invalid conversation SID");
      this.setError(copy.errors.invalidConversationSid);
      return false;
    }

    this.chatIdentity = identity;
    const client = new Client(token);

    client.on("initialized", async () => {
      try {
        // Setup Conversation
        this.conversation = await client.getConversationBySid(liveChatId);

        await this.initConversation(this.conversation);

        // Add token listeners only if the conversation init is OK
        client.on("tokenAboutToExpire", this.refreshAuthToken);

        // TODO: Ask for a new token for the same conversation... is it possible?
        client.on("tokenExpired", this.refreshAuthToken);

        this.isServiceReady = true;

        const conversationMetadata = await this.composeConversationMetadata(
          this.conversation,
          identity
        );

        const event = {
          ...conversationMetadata,
          isServiceReady: this.isServiceReady,
        } as ConversationServiceReadyEvent;

        this.listeners[ConversationEvents.Ready]?.forEach((listener) =>
          listener(event)
        );
      } catch (error: any) {
        console.error("initService", error.message);

        this.setError(copy.errors.conversationNotFound);
      }
    });

    // To catch client initialization errors, subscribe to the `'initFailed'` event.
    client.on("initFailed", (error) => {
      console.error("Fail on client SDK", error);
      this.setError(copy.errors.authToken.invalidToken);
    });

    // TODO: Should we manage this changes?
    client.on("connectionStateChanged", (state) => {
      console.log("Connection state changed:", state);
    });
    this.client = client;

    return true;
  };

  // Public way to send messages to conversations SDK
  sendMessage = async ({ body, files }: SendMessageData) => {
    if (!this.conversation) {
      return this.setError(copy.errors.conversationNotFound);
    }

    const msg = this.conversation.prepareMessage();
    body && msg.setBody(body);

    files?.forEach((file) => {
      msg.addMedia({
        media: file,
        contentType: file.type,
        filename: file.name,
      });
    });

    return msg.build().send();
  };

  startTyping = () => {
    this.conversation?.typing();
  };

  endLiveChat = async (chatEndedBy: chatEndedByValue): Promise<void> => {
    if (this.conversation && this.helpCenterApi) {
      await this.helpCenterApi.endLiveChat(this.conversation.sid, chatEndedBy);
    }
  };

  addEventListener = <ConversationEvent>(
    eventType: ConversationEventType,
    listener: Callback<ConversationEvent>
  ) => {
    if (!this.listeners[eventType]) {
      this.listeners[eventType] = [];
    }
    const array = this.listeners[eventType] as Callback<ConversationEvent>[];
    array.push(listener);
  };

  removeEventListener = <ConversationEvent>(
    eventType: ConversationEventType,
    listener: Callback<ConversationEvent>
  ) => {
    const array = this.listeners[eventType] as Callback<ConversationEvent>[];
    if (array) {
      const index = array.indexOf(listener) || -1;
      if (index > -1) {
        array.splice(index, 1);
      }
    }
  };
}

export default new ConversationService();
